import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarCheck, Target, ShieldCheck, Activity, LineChart } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";

const HeroScene = lazy(() => import("../three/HeroScene"));

const BADGES = [
  { icon: Target, label: "Goal Planning", cls: "left-[4%] top-[18%]" },
  { icon: ShieldCheck, label: "Protection Review", cls: "right-[2%] top-[10%]" },
  { icon: Activity, label: "Risk Profiling", cls: "left-[10%] bottom-[14%]" },
  { icon: LineChart, label: "SIP Projections", cls: "right-[6%] bottom-[22%]" },
];

function Badges() {
  return BADGES.map((b, i) => (
    <motion.div key={b.label} className={`glass-dark absolute hidden items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-200 md:flex ${b.cls}`}
      initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
      transition={{ opacity: { delay: 0.8 + i * 0.15 }, scale: { delay: 0.8 + i * 0.15 }, y: { duration: 4 + i, repeat: Infinity, ease: "easeInOut" } }}>
      <b.icon size={14} className="text-mint" /> {b.label}
    </motion.div>
  ));
}

export default function Hero() {
  const { settings } = useSettings();
  const headline = settings?.hero_headline || "Build Wealth. Protect Your Future. Invest Smarter.";
  const words = headline.split(" ");
  return (
    <section data-testid="hero-section" className="grain relative overflow-hidden bg-navy">
      <div className="grid-lines absolute inset-0" />
      <div className="aurora absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-mint/10 blur-[140px]" />
      <div className="aurora absolute right-0 top-0 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[140px]" />
      <div className="container-q relative grid min-h-[calc(100vh-72px)] items-center gap-6 py-14 lg:grid-cols-[1.05fr_1fr]">
        <div className="relative z-10">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="eyebrow text-gold">
            <span className="h-px w-8 bg-current" /> Your Future. Our Financial Intelligence.
          </motion.span>
          <h1 data-testid="hero-headline" className="mt-6 text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {words.map((w, i) => (
              <motion.span key={i} className={`mr-[0.25em] inline-block ${i >= words.length - 2 ? "gold-text" : ""}`}
                initial={{ opacity: 0, y: 30, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>{w}</motion.span>
            ))}
          </h1>
          <motion.p data-testid="hero-subheadline" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg">{settings?.hero_subheadline || "Discover intelligent financial solutions with Quantora. Get expert guidance on insurance, mutual funds, wealth planning and investment opportunities."}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to="/services" data-testid="hero-explore-services-btn" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-sm font-semibold text-navy transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(214,179,106,0.35)]">
              Explore Our Services <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/book-consultation" data-testid="hero-book-consultation-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-semibold text-white transition-[background-color,border-color] duration-300 hover:border-mint hover:bg-mint/10">
              <CalendarCheck size={16} className="text-mint" /> Book a Free Consultation
            </Link>
          </motion.div>
        </div>
        <div className="absolute inset-0 opacity-40 lg:relative lg:inset-auto lg:h-[620px] lg:opacity-100">
          <Suspense fallback={<div className="h-full w-full" />}><HeroScene /></Suspense>
          <Badges />
        </div>
      </div>
    </section>
  );
}
